/**
 * ReportsDrawer — top-nav bell that opens a right-side sheet listing
 * every report_job across every project the user can access.
 *
 * Auto-polls the ``/report-jobs`` unread count every 15 s so the badge
 * stays fresh even when the drawer is closed.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { FileText, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import MyReportsList from "@/components/reports/MyReportsList";


export default function ReportsDrawer() {
  const [open, setOpen] = useState(false);
  const [liveCount, setLiveCount] = useState(0);
  const [total, setTotal]         = useState(0);
  const nav = useNavigate();

  const refreshBadge = useCallback(async () => {
    try {
      const { data } = await api.get("/report-jobs", { params: { limit: 100 } });
      const arr = Array.isArray(data) ? data : [];
      setTotal(arr.length);
      setLiveCount(arr.filter((j) => j.status === "pending" || j.status === "running").length);
    } catch (e) {
      // silent — this is a passive badge poll
    }
  }, []);

  // Poll every 15 s so the header badge stays live.
  useEffect(() => {
    refreshBadge();
    const t = setInterval(refreshBadge, 15000);
    return () => clearInterval(t);
  }, [refreshBadge]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="topbar-reports-btn"
        aria-label="Open reports drawer"
        title="My Reports"
        className="relative h-10 w-10 grid place-items-center rounded-md hover:bg-accent transition-colors"
      >
        <FileText className="h-5 w-5" strokeWidth={1.75} />
        {liveCount > 0 && (
          <span
            className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 grid place-items-center
                       text-[9px] font-bold text-white bg-amber-500 rounded-full leading-none"
            data-testid="topbar-reports-live-count"
          >
            {liveCount}
          </span>
        )}
        {liveCount === 0 && total > 0 && (
          <span
            className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-primary"
            aria-hidden="true"
          />
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto rounded-none"
          data-testid="reports-drawer"
        >
          <SheetHeader className="pb-4 border-b border-border">
            <SheetTitle className="font-heading tracking-tight flex items-center gap-2">
              <FileText className="h-5 w-5" strokeWidth={1.5} />
              My Reports
            </SheetTitle>
            <p className="text-xs text-muted-foreground font-mono">
              Every PDF, ZIP and bundle job across every project you can access.
              Auto-refreshes while jobs are running.
            </p>
          </SheetHeader>
          <div className="py-4">
            <MyReportsList compact onNavigate={() => setOpen(false)} />
          </div>
          <div className="pt-2 border-t border-border">
            <Button
              onClick={() => { setOpen(false); nav("/reports"); }}
              variant="outline"
              className="rounded-none w-full"
              data-testid="reports-drawer-view-all"
            >
              Open full Reports page <ArrowRight className="h-3 w-3 ml-2" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
